import React, { useCallback, useState, useEffect, useMemo } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { fetchLinkedAccounts } from "../http/account.js";
import { Alert } from "../components/Ui/Alert.jsx";
import { formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";
import { fetchDistributorEyeTicketsAwaitingConfirmation } from "../http/distributor.js";
import { ExpandedRowContainer } from "../components/StyledComponents/Table/TableComponents.jsx";
import { FieldContainer } from "../components/StyledComponents/Forms/Forms.jsx";
import TextInputWithLabel from "../components/Inputs/TextInput.jsx";
import RichTextBoxWithLabel from "../components/Inputs/RichTextBoxWithLabel.jsx";
import AuthoriseEyeTicket from "../components/Ui/Distributor/AuthoriseEyeTicket.jsx";


const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  padding-top: 10px;
  margin: 0 0 20px 0;
  font-size:15px;
`;

const TableButton = styled.button`
  padding: 5px 5px;
  background-color: #007bff;
  color: white;
  font-size: 12px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

export default function AuthoriseEyeTickets({ colour, email }) {
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentEyeTicket, setCurrentEyeTicket] = useState(null);
  const [showAuthorise, setShowAuthorise] = useState(false);
  const [isTicketProcessed, setIsTicketProcessed] = useState(false);

  const [eyeTickets, setEyeTickets] = useState([]);
  const [eyeTicketCount, setEyeTicketCount] = useState(0);
  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts(email);
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
  }, [email]);


  const loadEyeTickets = useCallback(async (account, page, pageSize, filterStatus = null, searchValue = null, coWorker = null) => {
    setIsLoading(true);

    if (account || linkedAccounts) {
      try {
        const fetchedEyeTickets = await fetchDistributorEyeTicketsAwaitingConfirmation((account ? account : linkedAccounts[0].value), page, pageSize);

        setEyeTickets([]);
        setEyeTicketCount(0);

        if (fetchedEyeTickets && fetchedEyeTickets.OrdersAwaitingAuthorisation[0].Orders) {
          const restructuredEyeTickets = fetchedEyeTickets.OrdersAwaitingAuthorisation[0].Orders.map(eyeTicket => ({
            ...eyeTicket,
          }));

          setEyeTickets(restructuredEyeTickets);
          setEyeTicketCount(fetchedEyeTickets.OrdersAwaitingAuthorisation[0].TotalCount);

        }

        setError(null);
      } catch (e) {
        setError(e);
        setEyeTickets([]);
        setEyeTicketCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [email, linkedAccounts]);

  function ShowHideAuthorise(ticketId = null) {
    setShowAuthorise(!showAuthorise);

    if (ticketId) {
      setEyeTickets(prevEyeTickets =>
        prevEyeTickets.filter(ticket => ticket.UnqID !== ticketId)
      );
      setIsTicketProcessed(true);
    } else {
      setIsTicketProcessed(false);
    }
  }

  const columns = useMemo(() => [
    {
      Header: "Employee",
      accessor: "Employee",
    },
    {
      Header: "Date Created",
      accessor: row => formatDateToDDMMYYYY(row.DateCreated),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <TableButton onClick={() => {
          setCurrentEyeTicket(row.original);
          setShowAuthorise(true);
        }}>
          Authorise Order
        </TableButton>

      )
    },
  ]
  );

  const message = isTicketProcessed && currentEyeTicket
  ? (
    <>
      Successfully Authorised EyeTicket: <strong>{currentEyeTicket.OrderNumber}</strong> for <strong>{currentEyeTicket.Employee}</strong> belonging to <strong>{currentEyeTicket.CompanyName}</strong>. 
    </>
  )
  : 'Click on an order row to preview the EyeTicket';

  return (
    <>
      {showAuthorise &&
        <AuthoriseEyeTicket
        showHideModal={ShowHideAuthorise}
        isModalOpen={showAuthorise}
        colour={colour}
        eyeTicket={currentEyeTicket} />}
      <CenterContainer>
        <FormContainer>
          <Card
            label={'Authorise EyeTicket™ Orders'}
            headercolour={colour.sidebar}
            headerTextColour={colour.font}>
            <Heading>Order Authorisations</Heading>
            <Paragraph>Use the table below to view, and authorise your EyeTicket orders.</Paragraph>
            <ServerSidePagingTable
              columns={columns}
              pagingFn={loadEyeTickets}
              data={eyeTickets}
              recordCount={eyeTicketCount}
              setPagingTracker={setPagingTracker}
              arrAccountFilters={linkedAccounts}
              info={message}
              type={isTicketProcessed ? 'success' : 'info'}
              expand={(rowData) => (
                <ExpandedRowContainer>
                  <p style={{ fontSize: "1.1rem", marginBottom: "12px" }}>
                    <b>EyeTicket Summary for {rowData.Employee}</b>
                    <br></br>
                    Action Required: <b>{rowData.ActionRequired}</b>
                  </p>
                  <>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='Order No.'
                        value={rowData.OrderNumber}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='Company'
                        value={rowData.CompanyName}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label="Price For Items In Description"
                        type="number"
                        value={rowData?.PriceForItemsInDescription}
                        currency="£"
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label="Price For AddOns In Description"
                        type="number"
                        value={rowData?.PriceForAddOnsInDescription}
                        currency="£"
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <RichTextBoxWithLabel
                        label='Order Description'
                        value={rowData.Description}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='Optician'
                        value={rowData.Optician}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='Barcode'
                        value={rowData.Barcode}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='CostCentre'
                        value={rowData.CostCentre}
                        disabled />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel
                        label='Department'
                        value={rowData.Department}
                        disabled />
                    </FieldContainer>
                  </>
                </ExpandedRowContainer>
              )}
            >
            </ServerSidePagingTable>
            {(error && !linkedAccounts) && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
            {!eyeTicketCount && <Alert type="information">No EyeTickets found.</Alert>}
          </Card>
        </FormContainer>
      </CenterContainer>
    </>
  );
}